import React from "react";
import { useQuery } from "@tanstack/react-query";
import api, { money } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";

export const LEAD_SOURCES = [
  { value: "meta", label: "Meta / Facebook / Instagram" }, { value: "google", label: "Google Sheet / Google" },
  { value: "website", label: "Website" }, { value: "walk-in", label: "Walk-in" }, { value: "referral", label: "Referral" },
  { value: "phone", label: "Phone call" }, { value: "flex", label: "Flex board" }, { value: "pamphlet", label: "Pamphlet" },
  { value: "manual", label: "Manual" }, { value: "other", label: "Other" },
];
export const sourceLabel = (v) => LEAD_SOURCES.find((s) => s.value === v)?.label || v || "—";

const Field = ({ label, value, testid }) => (
  <div className="min-w-0">
    <div className="text-[11px] uppercase tracking-wide text-slate-400">{label}</div>
    <div className="text-sm font-medium break-words" data-testid={testid}>{value || "—"}</div>
  </div>
);

function MoneyBlock({ p }) {
  return (
    <div className="rounded-lg border p-3 space-y-2" data-testid="profile-money">
      <div className="grid grid-cols-3 gap-2">
        <Field label="Total fee" value={money(p.total_fee)} testid="profile-total" />
        <Field label="Paid" value={<span className="text-emerald-600">{money(p.paid)}</span>} testid="profile-paid" />
        <Field label="Balance" value={<span className={p.balance > 0 ? "text-red-600" : "text-emerald-600"}>{p.balance > 0 ? money(p.balance) : "Fully paid"}</span>} testid="profile-balance" />
      </div>
      <div className="space-y-1 max-h-40 overflow-y-auto">
        {(p.payments || []).map((x, i) => (
          <div key={i} className="flex justify-between text-xs border-b py-1" data-testid={`profile-pay-${i}`}>
            <span>#{i + 1} · {new Date(x.date).toLocaleDateString("en-IN")} · {x.method}{x.by ? ` · by ${x.by}` : ""}</span>
            <span className="font-semibold">{money(x.amount)} {x.status !== "verified" && <span className="text-amber-600">({x.status})</span>}</span>
          </div>
        ))}
        {!p.payments?.length && <p className="text-xs text-slate-400">No payments yet</p>}
      </div>
    </div>
  );
}

export function StudentProfile({ admissionId, onClose }) {
  const { data: p, isLoading } = useQuery({
    queryKey: ["profile", admissionId], enabled: !!admissionId,
    queryFn: async () => (await api.get(`/profiles/admission/${admissionId}`)).data,
  });
  return (
    <Dialog open={!!admissionId} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="student-profile">
        <DialogHeader><DialogTitle>{p ? `${p.name} · ${p.admission_no}` : "Student profile"}</DialogTitle></DialogHeader>
        {isLoading || !p ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <Field label="Phone" value={p.phone} testid="profile-phone" />
              <Field label="Email" value={p.email} testid="profile-email" />
              <Field label="Alt phone" value={p.alt_phone} />
              <Field label="Locality" value={p.locality} />
              <Field label="Occupation" value={p.occupation} />
              <Field label="Student no" value={p.student_no} />
              <Field label="Course" value={p.course_name} testid="profile-course" />
              <Field label="Batch" value={p.batch_name ? `${p.batch_name}${p.batch_schedule ? ` (${p.batch_schedule})` : ""}` : ""} testid="profile-batch" />
              <Field label="Joining date" value={p.joining_date} />
              <Field label="Lead source" value={sourceLabel(p.lead_source)} testid="profile-source" />
              <Field label="Sold by" value={p.sold_by} testid="profile-soldby" />
              <Field label="Admission done by" value={p.admission_by} testid="profile-admby" />
              <Field label="Admission status" value={p.status} />
              <Field label="Admission date" value={p.created_at ? new Date(p.created_at).toLocaleDateString("en-IN") : ""} />
              <Field label="Coupon" value={p.coupon_code} />
            </div>
            {p.sees_money && <MoneyBlock p={p} />}
            {p.notes && <div className="text-sm bg-slate-50 dark:bg-slate-900 rounded p-3" data-testid="profile-notes"><b>Notes:</b> {p.notes}</div>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
